import type {
  Account,
  Client,
  DashboardRecord,
  TextRecord,
  LogEntry,
  DumpRecord,
  Listing,
  Profile,
  Job,
  SiteVisitStat,
  GeneratedProfile,
} from "../types"

const API_BASE = "/api"

function getToken(): string | null {
  return localStorage.getItem("token")
}

function handleAuthError() {
  localStorage.removeItem("token")
  localStorage.removeItem("user")
  window.location.href = "/login"
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = { "Content-Type": "application/json" }
  if (token) headers["Authorization"] = `Bearer ${token}`

  const res = await fetch(`${API_BASE}${path}`, { headers, ...options })
  if (res.status === 401) {
    handleAuthError()
    throw new Error("Unauthorized")
  }
  if (!res.ok) {
    const body = await res.text().catch(() => "")
    throw new Error(`API ${res.status}: ${body || res.statusText}`)
  }
  return res.json()
}

export interface AccountOption {
  id: number
  accountId: string
  clientName: string
  strategy: string
  client: { id: number; fullName: string }
}

export type CreateDashboardDto = {
  accountId: string
  strategy: string
  objectId: string
  objectLink: string
  status: string
  terminDate?: string
  comment?: string
  cooperative?: string
  origin?: string
  source?: string
  mails?: { M2?: boolean; M3?: boolean; M4?: boolean; M5?: boolean; M6?: boolean }
}

export type UpdateDashboardDto = Partial<Omit<CreateDashboardDto, "accountId">>

export type CreateAccountDto = {
  clientId: number
  email?: string
  status: string
  strategy: string
  comment?: string
  eldersOnly?: boolean
  password?: string
  /** null clears the assigned proxy, a number assigns it. */
  proxyId?: number | null
}

export type UpdateAccountDto = Partial<Omit<CreateAccountDto, "accountId">>

export interface ProxyOption {
  id: number
  label: string
  host: string
  port: number
  country: string
  city: string
  enabled: boolean
  accountsCount: number
}

export type CreateProxyDto = {
  label: string
  host?: string
  port?: number
  country?: string
  city?: string
  enabled?: boolean
}

export type UpdateProxyDto = Partial<CreateProxyDto>

export interface ClientOption {
  id: number
  fullName: string
}

export type CreateClientDto = {
  fullName: string
  phone: string
  folderLink: string
  operator: string
  comment?: string
  price?: string
  rooms?: string
  sqft?: string
  location?: string
  documents?: string
  subscription?: string
  subscriptionDuration?: number
  subscriptionStart?: string
  subscriptionEnd?: string
  color?: string
  sex?: string | null
  birthName?: string
  birthPlaceCity?: string
  birthPlaceCountry?: string
  street?: string
  houseNumber?: string
  plz?: string
  city?: string
  country?: string
  profession?: string
  employer?: string
  monthlyNetIncome?: number | null
  additionalTenantInfo?: boolean
  additionalAdults?: boolean
  additionalMinors?: boolean
  additionalPersons?: {
    type: string
    firstName: string
    lastName: string
    birthday: string
    salutation?: string
    birthName?: string
    birthPlaceCity?: string
    street?: string
    houseNumber?: string
    plz?: string
    city?: string
    country?: string
    email?: string
    phone?: string
    profession?: string
    employer?: string
    monthlyNetIncome?: number | null
  }[]
  enabled?: boolean
  disabledReason?: string
}

export type UpdateClientDto = Partial<CreateClientDto>

export interface UserRecord {
  id: number
  email: string
  fullName: string
  role: "ADMIN" | "OPERATOR"
  enabled?: boolean
  createdAt?: string
  clientCount?: number
  accountCount?: number
  hasSecretCode?: boolean
}

export interface RevealAccountPasswordResponse {
  id: number
  password: string
  hasPassword: boolean
}

export interface GeneratedSecretCode {
  id: number
  fullName: string
  code: string
  generatedAt: string
}

export interface UserClientAccount {
  id: number
  accountId: string
  clientName: string
  email: string
  status: string
  strategy: string
  eldersOnly: boolean
}

export interface UserClient {
  id: number
  fullName: string
  color: string
  accounts: UserClientAccount[]
}

export interface UserClientsResponse {
  user: { id: number; fullName: string; email: string }
  clients: UserClient[]
  clientCount: number
  accountCount: number
}

export type CreateUserDto = {
  email: string
  password: string
  fullName: string
  role: "ADMIN" | "OPERATOR"
}

export interface OperatorRecord {
  id: number
  fullName: string
  email: string
  enabled?: boolean
}

export interface WorkerActivityRecord {
  userId: number
  fullName: string
  email: string
  role: string
  tabCount: number
  browsers: number
  online: boolean
  lastSeenAt: string
  sessionSeconds: number
  todayHeartbeats: number
  todayFirstSeenAt: string | null
  todayLastSeenAt: string | null
}

export interface WorkerDailyRecord {
  userId: number
  fullName: string
  email: string
  date: string
  heartbeatCount: number
  firstSeenAt: string | null
  lastSeenAt: string | null
}

export interface WorkerUserDay {
  date: string
  heartbeatCount: number
  firstSeenAt: string | null
  lastSeenAt: string | null
  hours: number[]
}

export interface WorkerAccountRecord {
  accountId: string
  clientName: string
  strategy: string
  heartbeatCount: number
  firstSeenAt: string | null
  lastSeenAt: string | null
}

export interface WorkerUserHistory {
  userId: number
  fullName: string
  email: string
  daily: WorkerUserDay[]
  accounts: WorkerAccountRecord[]
}

export interface MailTemplateRecord {
  id: number
  client: string
  clientId: number
  agentName: string
  notes: string
  mails: { id: number; type: string; german: string; ukrainian: string }[]
}

export type CreateMailDto = {
  clientId: number
  agentName?: string
  notes?: string
  mails?: { type: string; german?: string; ukrainian?: string }[]
}

export type UpdateMailDto = {
  agentName?: string
  notes?: string
}

export type UpdateMailContentDto = {
  german?: string
  ukrainian?: string
}

export type GenerateMailDto = {
  type: string
  clientId: number
  instructions?: string
  maxLength?: number
  agentName?: string
  letterDate?: string
}

export type CreateProfileDto = {
  name: string
  maxRent?: number
  minRooms?: number
  maxRooms?: number
  minSize?: number
  maxSize?: number
  familySize?: number
  desireLocation?: string[]
  goodLocation?: string[]
  optimallyLocation?: string[]
  notes?: string
}

export type UpdateProfileDto = Partial<CreateProfileDto & { active: boolean }>

export type CreateLogDto = {
  user: string
  page: string
  action: "add" | "edit" | "queued"
  details: string
  client?: string
  clientId?: string
}

export interface LogRecord {
  id: number
  timestamp: string
  user: string
  page: string
  action: string
  details: string
  client: string
  clientId: string
  accountEmail: string
}

export interface SendReportResponse {
  ok: boolean
  client: { id: number; fullName: string }
  records: number
  chatId: number
  threadId: number | null
  fileName: string
}

export interface LogDuplicateGroup {
  accountId: string
  client: string
  page: string
  action: string
  details: string
  count: number
  keepId: number
  removeIds: number[]
}

export interface LogDuplicatesResponse {
  total: number
  groups: LogDuplicateGroup[]
}

export type BotDestination = "client" | "central"

export interface BotSettings {
  enabled: boolean
  beforeMinutes: number[]
  afterMinutes: number[]
  destination: BotDestination
  centralChatId: string
  centralThreadId: string
  source: "database" | "env"
  timeZone: string
  botConfigured: boolean
  updatedAt: string | null
}

export type UpdateBotSettingsDto = {
  enabled: boolean
  beforeMinutes: number[]
  afterMinutes: number[]
  destination: BotDestination
  centralChatId?: string
  centralThreadId?: string
}

export interface BotClientChat {
  clientId: number
  fullName: string
  active: boolean
  chatId: string
  threadId: string
  chatEnabled: boolean
  source: "database" | "default" | "none"
}

export interface UpdateBotClientChatDto {
  chatId: string
  threadId?: string
  enabled?: boolean
}

export interface ScheduledMessage {
  id: number
  clientId: number | null
  chatId: string
  threadId: string
  message: string
  sendAt: string
  status: "PENDING" | "SENT" | "FAILED" | "CANCELLED" | string
  sentAt: string | null
  error: string
  createdBy: string
  createdAt: string
  client?: { id: number; fullName: string } | null
}

export type ScheduleBotMessageDto = {
  clientId?: number
  chatId?: string
  threadId?: string
  message: string
  sendAt: string
}

export type TestBotMessageDto = {
  clientId?: number
  chatId?: string
  threadId?: string
  text?: string
}

export const api = {
  getDashboard: () => request<DashboardRecord[]>("/dashboard"),
  getArchived: () => request<DashboardRecord[]>("/dashboard/archived"),
  createDashboard: (dto: CreateDashboardDto) =>
    request<DashboardRecord>("/dashboard", { method: "POST", body: JSON.stringify(dto) }),
  updateDashboard: (id: number, dto: UpdateDashboardDto) =>
    request<DashboardRecord>(`/dashboard/${id}`, { method: "PUT", body: JSON.stringify(dto) }),
  archiveDashboard: (id: number) =>
    request<DashboardRecord>(`/dashboard/${id}/archive`, { method: "PUT" }),
  deleteDashboard: (id: number) =>
    request<{ id: number }>(`/dashboard/${id}`, { method: "DELETE" }),

  getAccounts: () => request<AccountOption[]>("/accounts"),
  getAllAccounts: () => request<Account[]>("/accounts"),
  createAccount: (dto: CreateAccountDto) =>
    request<Account>("/accounts", { method: "POST", body: JSON.stringify(dto) }),
  updateAccount: (id: number, dto: UpdateAccountDto) =>
    request<Account>(`/accounts/${id}`, { method: "PUT", body: JSON.stringify(dto) }),
  archiveAccount: (id: number) => request<Account>(`/accounts/${id}/archive`, { method: "PUT" }),
  deleteAccount: (id: number) => request<{ id: number }>(`/accounts/${id}`, { method: "DELETE" }),
  revealAccountPassword: (id: number, secretCode?: string) =>
    request<RevealAccountPasswordResponse>(`/accounts/${id}/reveal-password`, {
      method: "POST",
      cache: "no-store",
      body: JSON.stringify(secretCode ? { secretCode } : {}),
    }),

  getProxies: () => request<ProxyOption[]>("/proxies"),
  createProxy: (dto: CreateProxyDto) =>
    request<ProxyOption>("/proxies", { method: "POST", body: JSON.stringify(dto) }),
  updateProxy: (id: number, dto: UpdateProxyDto) =>
    request<ProxyOption>(`/proxies/${id}`, { method: "PUT", body: JSON.stringify(dto) }),
  deleteProxy: (id: number) =>
    request<{ id: number; label: string }>(`/proxies/${id}`, { method: "DELETE" }),

  getClients: () => request<ClientOption[]>("/clients/light"),
  getAllClients: () => request<Client[]>("/clients"),
  createClient: (dto: CreateClientDto) =>
    request<Client>("/clients", { method: "POST", body: JSON.stringify(dto) }),
  updateClient: (id: number, dto: UpdateClientDto) =>
    request<Client>(`/clients/${id}`, { method: "PUT", body: JSON.stringify(dto) }),
  generateProfiles: (clientId: number) =>
    request<{ profiles: GeneratedProfile[] }>(`/clients/${clientId}/generate-profiles`, {
      method: "POST",
    }),

  getUsers: () => request<UserRecord[]>("/auth/users"),
  getUserClients: (id: number) => request<UserClientsResponse>(`/auth/users/${id}/clients`),
  getOperators: () => request<string[]>("/auth/operators"),
  createUser: (dto: CreateUserDto) =>
    request<UserRecord>("/auth/register", { method: "POST", body: JSON.stringify(dto) }),
  deleteUser: (id: number) => request<UserRecord>(`/auth/users/${id}`, { method: "DELETE" }),
  setUserEnabled: (id: number, enabled: boolean) =>
    request<UserRecord>(`/auth/users/${id}/enabled`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }),
  generateUserSecretCode: (id: number) =>
    request<GeneratedSecretCode>(`/auth/users/${id}/reveal-code`, {
      method: "POST",
      cache: "no-store",
    }),

  getMails: () => request<MailTemplateRecord[]>("/mails"),
  createMail: (dto: CreateMailDto) =>
    request<MailTemplateRecord>("/mails", { method: "POST", body: JSON.stringify(dto) }),
  updateMail: (id: number, dto: UpdateMailDto) =>
    request<MailTemplateRecord>(`/mails/${id}`, { method: "PUT", body: JSON.stringify(dto) }),
  updateMailContent: (templateId: number, mailId: number, dto: UpdateMailContentDto) =>
    request<MailTemplateRecord>(`/mails/${templateId}/mail/${mailId}`, {
      method: "PUT",
      body: JSON.stringify(dto),
    }),
  generateMail: (dto: GenerateMailDto) =>
    request<{ german: string; ukrainian: string }>("/mails/generate", {
      method: "POST",
      body: JSON.stringify(dto),
    }),

  getLogs: () => request<LogRecord[]>("/logs"),
  createLog: (dto: CreateLogDto) =>
    request<LogRecord>("/logs", { method: "POST", body: JSON.stringify(dto) }),
  clearLogs: (client?: string) =>
    request<{ deleted: number }>(`/logs${client ? `?client=${encodeURIComponent(client)}` : ""}`, {
      method: "DELETE",
    }),
  sendClientReport: (clientName: string) =>
    request<SendReportResponse>("/reports/send", {
      method: "POST",
      body: JSON.stringify({ clientName }),
    }),
  getLogDuplicates: (client?: string) =>
    request<LogDuplicatesResponse>(
      `/logs/duplicates${client ? `?client=${encodeURIComponent(client)}` : ""}`,
    ),
  removeLogDuplicates: (client?: string) =>
    request<{ deleted: number }>(
      `/logs/duplicates${client ? `?client=${encodeURIComponent(client)}` : ""}`,
      { method: "DELETE" },
    ),

  getOperatorsFull: () => request<OperatorRecord[]>("/auth/operators/full"),

  getDumps: () => request<DumpRecord[]>("/dumps"),
  deleteDump: (id: number) => request<{ id: number }>(`/dumps/${id}`, { method: "DELETE" }),

  getListings: (params?: {
    status?: string
    sourceName?: string
    limit?: number
    offset?: number
  }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set("status", params.status)
    if (params?.sourceName) qs.set("sourceName", params.sourceName)
    if (params?.limit) qs.set("limit", String(params.limit))
    if (params?.offset) qs.set("offset", String(params.offset))
    const q = qs.toString()
    return request<{ items: Listing[]; total: number }>(`/listings${q ? `?${q}` : ""}`)
  },
  getListingStats: () =>
    request<{ total: number; new: number; matched: number; processed: number; skipped: number }>(
      "/listings/stats",
    ),
  getListing: (id: number) => request<Listing>(`/listings/${id}`),

  getProfiles: () => request<Profile[]>("/profiles"),
  getProfile: (id: number) => request<Profile>(`/profiles/${id}`),
  createProfile: (dto: CreateProfileDto) =>
    request<Profile>("/profiles", { method: "POST", body: JSON.stringify(dto) }),
  updateProfile: (id: number, dto: UpdateProfileDto) =>
    request<Profile>(`/profiles/${id}`, { method: "PUT", body: JSON.stringify(dto) }),
  toggleProfile: (id: number) => request<Profile>(`/profiles/${id}/toggle`, { method: "PUT" }),
  deleteProfile: (id: number) => request<{ id: number }>(`/profiles/${id}`, { method: "DELETE" }),
  deleteAllProfiles: () => request<{ deleted: number }>("/profiles", { method: "DELETE" }),
  syncProfilesFromClients: () =>
    request<{ created: number; updated: number; profiles: Profile[] }>("/profiles/sync", {
      method: "POST",
    }),

  getJobs: (params?: { status?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set("status", params.status)
    if (params?.limit) qs.set("limit", String(params.limit))
    if (params?.offset) qs.set("offset", String(params.offset))
    const q = qs.toString()
    return request<{ items: Job[]; total: number }>(`/jobs${q ? `?${q}` : ""}`)
  },
  getJobStats: () =>
    request<{ total: number; pending: number; claimed: number; completed: number; failed: number }>(
      "/jobs/stats",
    ),
  clearPendingJobs: () => request<{ deleted: number }>("/jobs/pending", { method: "DELETE" }),

  getSiteVisits: (days?: number) =>
    request<SiteVisitStat[]>(`/visits/stats${days ? `?days=${days}` : ""}`),

  getWorkerActivity: () => request<WorkerActivityRecord[]>("/worker/activity"),
  getWorkerActivityHistory: (days?: number) =>
    request<WorkerDailyRecord[]>(`/worker/activity/history${days ? `?days=${days}` : ""}`),
  getWorkerActivityHistoryForUser: (userId: number, days?: number) =>
    request<WorkerUserHistory>(`/worker/activity/${userId}/history${days ? `?days=${days}` : ""}`),

  getBotSettings: () => request<BotSettings>("/bot/settings"),
  updateBotSettings: (dto: UpdateBotSettingsDto) =>
    request<BotSettings>("/bot/settings", { method: "PUT", body: JSON.stringify(dto) }),
  getBotClients: () => request<BotClientChat[]>("/bot/clients"),
  updateBotClientChat: (clientId: number, dto: UpdateBotClientChatDto) =>
    request<{ clientId: number; chatId: string; threadId: string; chatEnabled: boolean }>(
      `/bot/clients/${clientId}`,
      { method: "PUT", body: JSON.stringify(dto) },
    ),
  getBotMessages: () => request<ScheduledMessage[]>("/bot/messages"),
  scheduleBotMessage: (dto: ScheduleBotMessageDto) =>
    request<ScheduledMessage>("/bot/messages", { method: "POST", body: JSON.stringify(dto) }),
  cancelBotMessage: (id: number) =>
    request<ScheduledMessage>(`/bot/messages/${id}`, { method: "DELETE" }),
  testBotMessage: (dto: TestBotMessageDto) =>
    request<{ success: boolean }>("/bot/test", { method: "POST", body: JSON.stringify(dto) }),
}
